import type { SelectOption } from '@libs/ui/ds'
import { AddBoldIcon, Button, Flex, IconWrap, Text } from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import { MealTypesLegacy } from '@spa/constants/MealTypes'
import { cva } from 'class-variance-authority'
import type { FC } from 'react'
import React, { useCallback } from 'react'
import type { Control } from 'react-hook-form'
import { Controller, useFieldArray } from 'react-hook-form'

import type { MealValue } from '../MealRow/MealRow'
import { MealRowLegacy } from '../MealRow/MealRowLegacy'

type Props = {
  control?: Control<any>
  defaultSelectedOption?: string
  isShiftLocked?: boolean
  isManager?: boolean
  canEmployeeEdit?: boolean
  real?: boolean
}

const containerCVA = cva([], {
  variants: {
    isManager: {
      false: 'flex items-baseline'
    }
  }
})

/**
 * @deprecated file
 */

export const MealRowsContainerLegacy: FC<Props> = ({
  control,
  defaultSelectedOption,
  isShiftLocked,
  isManager = true,
  canEmployeeEdit = false,
  real = false
}) => {
  const { t, tCommon } = usePlanningFormTranslation()
  const mealTypesOptions: SelectOption[] = MealTypesLegacy.valueSeq()
    .map(mealType => ({
      value: mealType?.id,
      label: tCommon(mealType?.name ?? ''),
      leftIcon: mealType?.icon
    }))
    .toJS()

  const { fields, append, remove, update } = useFieldArray<{
    meals: { value: MealValue }[]
  }>({
    control,
    name: 'meals'
  })

  const onRemove = useCallback(
    index => {
      if (isManager) {
        remove(index)
      }
    },
    [isManager, remove]
  )

  const defaultMeal = mealTypesOptions.find(
    meal => meal.value === defaultSelectedOption
  )

  const addMeal = () => {
    append({
      value: { meal: { ...defaultMeal }, checked: true, initialMeal: undefined }
    })
  }

  const handleChange = (newValue: MealValue, index: number) => {
    update(index, { value: newValue })
  }

  if (fields.length === 0 && !isManager && !canEmployeeEdit) {
    return null
  }

  return (
    <div className='mb-2'>
      <Text size='information' className='mb-2 font-medium text-neutral-800'>
        {t('component.mealRow.title')}
      </Text>
      <Controller
        name='meals'
        control={control}
        render={({ field: { value } }) => (
          <>
            {value.map((mealItem, index) => (
              <div
                className={containerCVA({ isManager })}
                key={`${mealItem?.value?.id}_${index}`}
              >
                <MealRowLegacy
                  mealTypesOptions={mealTypesOptions}
                  key={index}
                  value={mealItem.value}
                  inputName={`meals.${index}.value` as const}
                  isShiftLocked={isShiftLocked}
                  disabled={isShiftLocked || !isManager}
                  onRemove={() => onRemove(index)}
                  onChange={(newValue: MealValue) =>
                    handleChange(newValue, index)
                  }
                  isManager={isManager}
                  canEmployeeEdit={canEmployeeEdit}
                  real={real}
                />
              </div>
            ))}
          </>
        )}
      />
      {isManager && (
        <Flex className='items-start'>
          <Button
            variant='tertiary'
            intent='neutral-dark'
            leftIcon={<IconWrap icon={<AddBoldIcon />} size='xs' />}
            onClick={addMeal}
            disabled={isShiftLocked}
          >
            <Text>{t('component.mealRow.add')}</Text>
          </Button>
        </Flex>
      )}
    </div>
  )
}
